import { useState, useEffect } from 'react'
import { X, Check, Bluetooth, ChevronRight, ChevronLeft, Eye, EyeOff } from 'lucide-react'
import type { Property, Team, User, QRCodeConfigResponse } from '../../../types/api.types'
import { useAuthStore } from '../../auth/store/authStore'
import { buildProvisioningPayload, type ProvisioningPayload } from '../utils/provisioningPayload'
import {
  PROVISIONING_SERVICE_UUID,
  CHAR_PROVISION_WRITE_UUID,
  CHAR_STATUS_UUID,
  QUICKCOMM_SETUP_LAST_SSID_KEY,
  QUICKCOMM_SETUP_LAST_DEVICE_NAME_KEY,
  QUICKCOMM_SETUP_LAST_LOCAL_IP_KEY,
  QUICKCOMM_SETUP_LAST_LOCAL_PORT_KEY,
  DEFAULT_PRINTER_PORT,
} from '../constants/adminConstants'

// ── Types ──

type BleDeviceProvisionResponse = {
  ok: boolean
  stage?: string
  error?: string
}

// ── Helpers ──

const BLE_LOG_PREFIX = '[QuickComm device setup]'

function bleDebug(message: string, detail?: unknown) {
  if (detail !== undefined) {
    console.log(`${BLE_LOG_PREFIX} ${message}`, detail)
  } else {
    console.log(`${BLE_LOG_PREFIX} ${message}`)
  }
}

function payloadForLog(p: ProvisioningPayload): Record<string, unknown> {
  const out: Record<string, unknown> = { ...p }
  if (out.password !== undefined) {
    out.password = p.password ? '[REDACTED]' : undefined
  }
  return out
}

function readStored(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) || fallback
  } catch {
    return fallback
  }
}

function readStoredPort(key: string, fallback: number): number {
  const n = parseInt(readStored(key, ''), 10)
  return Number.isNaN(n) ? fallback : n
}

function logBleError(context: string, err: unknown) {
  const e = err as Error & { code?: number }
  console.error(`${BLE_LOG_PREFIX} ${context}`, {
    name: e?.name,
    message: e?.message,
    stack: e?.stack,
    code: typeof e?.code === 'number' ? e.code : undefined,
  })
}

// ── Component ──

interface QuickCommSetupModalProps {
  open: boolean
  onClose: () => void
  deviceData: QRCodeConfigResponse
  properties: Property[]
  teams: Team[]
  users: User[]
}

export function QuickCommSetupModal({
  open,
  onClose,
  deviceData,
  properties,
  teams,
  users,
}: QuickCommSetupModalProps): JSX.Element | null {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [qrConfig, setQrConfig] = useState({
    ssid: '',
    password: '',
    device_name: 'QuickComm',
    comm_mode: 'udp',
    mode: 'cloud',
    local_ip: '',
    local_port: 8000,
  })
  const [bleStatus, setBleStatus] = useState<string | null>(null)
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | null>(null)
  const [doneMessage, setDoneMessage] = useState<string | null>(null)
  const [showWifiPassword, setShowWifiPassword] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Only this user may target a local (on-network) server; everyone else is cloud-only
  const user = useAuthStore((s) => s.user)
  const canUseLocalServer = user?.email === 'townhouse@gmail.com'

  const property = properties.find(p => p.id === deviceData.property_id)

  // A property has one printer per kitchen now, and this device points at exactly
  // one of them. Whoever opened setup resolved the id to an address — this modal
  // has no printers list, and the firmware wants a host, not an id.
  const printerHost = deviceData.printer_ip ?? ''
  const printerPort = deviceData.printer_port ?? DEFAULT_PRINTER_PORT

  // Restore saved SSID and device name on open
  useEffect(() => {
    if (!open) return
    setStep(1)
    setBleStatus(null)
    setSelectedDeviceName(null)
    setDoneMessage(null)
    setShowAdvanced(false)
    setShowWifiPassword(false)
    setError(null)
    setLoading(false)

    setQrConfig({
      ssid: readStored(QUICKCOMM_SETUP_LAST_SSID_KEY, ''),
      password: '',
      device_name: readStored(QUICKCOMM_SETUP_LAST_DEVICE_NAME_KEY, 'QuickComm'),
      comm_mode: 'udp',
      mode: 'cloud',
      local_ip: readStored(QUICKCOMM_SETUP_LAST_LOCAL_IP_KEY, ''),
      local_port: readStoredPort(QUICKCOMM_SETUP_LAST_LOCAL_PORT_KEY, 8000),
    })
  }, [open])

  if (!open) return null

  // ── BLE provisioning ──

  async function sendProvisioningOverBLE(
    payload: ProvisioningPayload
  ): Promise<'confirmed' | 'sent-assumed-ok'> {
    bleDebug('start', {
      hasBluetooth: !!navigator.bluetooth,
      payloadPreview: payloadForLog(payload),
    })

    if (!navigator.bluetooth) {
      throw new Error(
        'Wireless setup is not available in this browser. Use Chrome or Edge on a computer, then try again.'
      )
    }

    setBleStatus('Looking for your QuickComm device…')

    let device: BluetoothDevice
    try {
      device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [PROVISIONING_SERVICE_UUID] }],
        optionalServices: ['battery_service', 'device_information'],
      })
    } catch (e) {
      logBleError('requestDevice failed', e)
      throw e
    }

    setSelectedDeviceName(device.name || device.id || 'QuickComm device')
    setBleStatus('Connecting to your device…')

    let gattServer: BluetoothRemoteGATTServer
    try {
      gattServer = (await device.gatt?.connect()) as BluetoothRemoteGATTServer
    } catch (e) {
      logBleError('gatt.connect failed', e)
      throw e
    }

    if (!gattServer) {
      throw new Error('Could not connect to your QuickComm device. Move closer and try again.')
    }

    try {
      let service: BluetoothRemoteGATTService
      try {
        service = await gattServer.getPrimaryService(PROVISIONING_SERVICE_UUID)
      } catch (e) {
        logBleError(`getPrimaryService(${PROVISIONING_SERVICE_UUID}) failed`, e)
        throw e
      }

      let writeCharacteristic: BluetoothRemoteGATTCharacteristic
      try {
        writeCharacteristic = await service.getCharacteristic(CHAR_PROVISION_WRITE_UUID)
      } catch (e) {
        logBleError(`getCharacteristic(write) failed`, e)
        throw e
      }

      const writeProps = writeCharacteristic.properties

      const jsonString = JSON.stringify(payload)
      const encodedPayload = new TextEncoder().encode(jsonString)

      setBleStatus('Sending Wi‑Fi and account settings…')

      type WritableCharacteristic = BluetoothRemoteGATTCharacteristic & {
        writeValueWithResponse?: (value: BufferSource) => Promise<void>
      }
      const writableChar = writeCharacteristic as WritableCharacteristic

      if (!writeProps.write && !writeProps.writeWithoutResponse) {
        throw new Error(
          'This QuickComm device is not accepting settings right now. It may need an update—contact support if this keeps happening.'
        )
      }

      const isNotSupportedGattError = (e: unknown): boolean => {
        if (!e || typeof e !== 'object') return false
        return 'name' in e && String((e as Error).name) === 'NotSupportedError'
      }

      try {
        if (writeProps.write) {
          if (typeof writableChar.writeValueWithResponse !== 'function') {
            throw new Error(
              'This browser cannot send settings to the device wirelessly. Try Chrome or Edge on a computer.'
            )
          }
          await writableChar.writeValueWithResponse!(encodedPayload)
        } else if (writeProps.writeWithoutResponse) {
          if (typeof writeCharacteristic.writeValueWithoutResponse !== 'function') {
            throw new Error('Could not send settings to this device. Try again or use another browser.')
          }
          await writeCharacteristic.writeValueWithoutResponse(encodedPayload)
        }
      } catch (e) {
        if (isNotSupportedGattError(e)) {
          logBleError('write failed with NotSupportedError — assuming payload may still have been received', e)
          setBleStatus('Setup completed.')
          return 'sent-assumed-ok'
        }
        logBleError('write failed', e)
        throw e
      }

      // Brief delay for firmware to update GATT value
      await new Promise<void>(resolve => setTimeout(resolve, 100))

      const isDeviceRejection = (err: unknown): boolean =>
        err instanceof Error && err.message.startsWith('Device rejected provisioning')

      try {
        let statusCharacteristic: BluetoothRemoteGATTCharacteristic
        try {
          statusCharacteristic = await service.getCharacteristic(CHAR_STATUS_UUID)
        } catch (e) {
          logBleError(`getCharacteristic(status) failed`, e)
          throw e
        }

        let rawStatus: string
        try {
          const dataView = await statusCharacteristic.readValue()
          rawStatus = new TextDecoder().decode(dataView.buffer)
        } catch (e) {
          logBleError('readValue on status characteristic failed', e)
          throw e
        }

        let deviceStatus: BleDeviceProvisionResponse
        try {
          deviceStatus = JSON.parse(rawStatus) as BleDeviceProvisionResponse
        } catch (e) {
          logBleError('parse device status JSON', e)
          throw e
        }

        if (!deviceStatus.ok) {
          const errCode = deviceStatus.error || 'unknown'
          throw new Error(
            errCode === 'invalid_json'
              ? 'Device rejected provisioning: Your QuickComm device could not read the settings. Check your entries and try again.'
              : `Device rejected provisioning: Your device reported a problem (${errCode}).`
          )
        }

        if (deviceStatus.stage === 'provisioned') {
          setBleStatus('Your QuickComm device is set up.')
        } else {
          setBleStatus(
            deviceStatus.stage ? `Device status: ${deviceStatus.stage}` : 'Your device responded successfully.'
          )
        }

        return 'confirmed'
      } catch (e) {
        if (isDeviceRejection(e)) throw e
        logBleError('post-write status unavailable (treating as done)', e)
        setBleStatus('Setup completed.')
        return 'sent-assumed-ok'
      }
    } finally {
      if (device.gatt?.connected) {
        device.gatt.disconnect()
      }
    }
  }

  // ── Send handler ──

  async function handleSend() {
    if (!deviceData) return

    setLoading(true)
    setError(null)
    setDoneMessage(null)
    setBleStatus(null)

    try {
      // Inside the try: this throws on a device with no property, and out here
      // that would have been an unhandled rejection instead of a visible message.
      const payload = buildProvisioningPayload(deviceData, {
        ...qrConfig,
        printer_ip: printerHost,
        printer_port: printerPort,
      })
      const outcome = await sendProvisioningOverBLE(payload)

      try {
        if (payload.ssid?.trim()) {
          localStorage.setItem(QUICKCOMM_SETUP_LAST_SSID_KEY, payload.ssid.trim())
        }
        if (payload.device_name?.trim()) {
          localStorage.setItem(QUICKCOMM_SETUP_LAST_DEVICE_NAME_KEY, payload.device_name.trim())
        }
        if (payload.local_ip) {
          localStorage.setItem(QUICKCOMM_SETUP_LAST_LOCAL_IP_KEY, payload.local_ip)
          localStorage.setItem(QUICKCOMM_SETUP_LAST_LOCAL_PORT_KEY, String(payload.local_port))
        }
      } catch { /* ignore */ }

      setDoneMessage(
        outcome === 'confirmed'
          ? 'Your QuickComm device confirmed that setup finished successfully.'
          : "Your settings were sent to the device. Sometimes the browser can\u2019t show a final \u201COK\u201D even when the device received everything\u2014this is normal. Check Wi\u2011Fi or status on the QuickComm itself. If something looks wrong, try sending again or contact support."
      )
      setStep(3)
    } catch (err) {
      logBleError('handleSend (top-level catch)', err)
      const errorMessage = err instanceof Error
        ? err.message
        : 'Could not finish wireless setup. Please try again.'
      if (errorMessage.toLowerCase().includes('user cancelled')) {
        setError('You closed the device picker. Tap "Send to QuickComm device" when you are ready to try again.')
      } else {
        setError(errorMessage)
      }
      setBleStatus('Setup did not finish.')
    } finally {
      setLoading(false)
    }
  }

  // ── Resolve labels ──

  const propertyName = property?.name || `ID ${deviceData.property_id}`
  const isRestaurant = property?.property_type === 'RESTAURANT'
  const teamName = deviceData.team_id ? (teams.find(t => t.id === deviceData.team_id)?.name || `ID ${deviceData.team_id}`) : null
  const tableLabel = deviceData.table_number != null ? `Table ${deviceData.table_number}` : deviceData.table_id != null ? 'Assigned' : null
  const userName = deviceData.user_id ? (users.find(u => u.id === deviceData.user_id)?.name || `ID ${deviceData.user_id}`) : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-3 sm:p-4">
      <div className="my-4 w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-4 sm:my-8 sm:p-6">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {step === 3 ? (
              <>
                <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <Check className="shrink-0 text-brand-green" size={24} />
                  Device connection complete
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                  Your device is ready. Use Done to return to Manage Devices.
                </p>
              </>
            ) : (
              <>
                <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <Bluetooth size={24} className="text-brand-green" />
                  Set up your QuickComm device
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                  Send Wi‑Fi and account details from this browser to your device over Bluetooth—no cables needed.
                </p>
              </>
            )}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        {step !== 3 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
            <span className={step === 1 ? 'font-semibold text-brand-green' : 'text-gray-400'}>1 · Device</span>
            <span className="text-gray-300">→</span>
            <span className={step === 2 ? 'font-semibold text-brand-green' : 'text-gray-400'}>2 · Wi‑Fi & send</span>
            <span className="text-gray-300">→</span>
            <span className="text-gray-400">3 · Complete</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{error}</div>
        )}

        <div className="space-y-4">
          {/* ── Step 3: Complete ── */}
          {step === 3 && (
            <div className="space-y-5 py-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                <Check className="text-brand-green" size={36} />
              </div>
              <div className="mx-auto max-w-lg space-y-2">
                {doneMessage && <p className="text-sm text-gray-600">{doneMessage}</p>}
                <p className="pt-1 text-xs text-gray-400">
                  Tip: wait until the device shows it's on Wi‑Fi before you unplug or move it.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-brand-green px-10 py-3 font-semibold text-black transition-opacity hover:opacity-90"
              >
                Done
              </button>
            </div>
          )}

          {/* ── Step 1: Device info ── */}
          {step === 1 && (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <h3 className="mb-3 font-semibold text-gray-900">Before you start</h3>
                <ul className="space-y-2.5 text-sm text-gray-600">
                  <li className="flex gap-2.5">
                    <Check className="mt-0.5 shrink-0 text-brand-green" size={16} />
                    <span>
                      Use <strong className="text-gray-800">Chrome or Edge</strong> on this computer (required for wireless setup to your QuickComm).
                    </span>
                  </li>
                  <li className="flex gap-2.5">
                    <Check className="mt-0.5 shrink-0 text-brand-green" size={16} />
                    <span>
                      Turn on <strong className="text-gray-800">Bluetooth</strong> and stay within a few feet of the device.
                    </span>
                  </li>
                  <li className="flex gap-2.5">
                    <Check className="mt-0.5 shrink-0 text-brand-green" size={16} />
                    <span>
                      On the next step, when you send settings, your browser will ask you to choose a device—pick <strong className="text-gray-800">QuickComm</strong> (or the name shown on the hardware).
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-2 font-semibold text-gray-900">This device</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-gray-500">MAC Address</p>
                  <p className="font-mono text-gray-900">{deviceData.mac_address}</p>
                  <p className="text-gray-500">Property</p>
                  <p className="text-gray-900">{propertyName}</p>
                  {isRestaurant && (
                    <>
                      <p className="text-gray-500">Table</p>
                      <p className="text-gray-900">{tableLabel ?? 'Not linked to a table'}</p>
                    </>
                  )}
                  {teamName && (
                    <>
                      <p className="text-gray-500">Team</p>
                      <p className="text-gray-900">{teamName}</p>
                    </>
                  )}
                  {userName && (
                    <>
                      <p className="text-gray-500">User</p>
                      <p className="text-gray-900">{userName}</p>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green px-4 py-3 font-semibold text-black transition-opacity hover:opacity-90"
              >
                Continue to Wi‑Fi settings
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* ── Step 2: Wi-Fi & send ── */}
          {step === 2 && (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="-mt-1 mb-1 flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700"
              >
                <ChevronLeft size={16} />
                Back to device
              </button>

              <p className="-mt-1 mb-2 text-xs text-gray-400">
                Wi‑Fi network name is remembered on this browser only (not your password).
              </p>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-4 font-semibold text-gray-900">Wi‑Fi & device name</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm text-gray-500">Network name (SSID)</label>
                      <input
                        type="text"
                        value={qrConfig.ssid}
                        onChange={e => setQrConfig({ ...qrConfig, ssid: e.target.value })}
                        autoComplete="off"
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                        placeholder="e.g. Restaurant-Guest"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-500">Network password</label>
                      <div className="relative">
                        <input
                          type={showWifiPassword ? 'text' : 'password'}
                          value={qrConfig.password}
                          onChange={e => setQrConfig({ ...qrConfig, password: e.target.value })}
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-11 text-gray-900 outline-none focus:border-brand-green"
                          placeholder="Wi‑Fi password"
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowWifiPassword(v => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          aria-label={showWifiPassword ? 'Hide password' : 'Show password'}
                        >
                          {showWifiPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-500">Name on device</label>
                    <input
                      type="text"
                      value={qrConfig.device_name}
                      onChange={e => setQrConfig({ ...qrConfig, device_name: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                      placeholder="QuickComm"
                    />
                  </div>

                  {/* Read-only on purpose. The route lives on the device record, so a
                      value typed here would be sent to this one unit and then contradicted
                      by the printer the backend routes its tickets to. */}
                  {isRestaurant && (
                    <div className="space-y-2 border-t border-gray-200 pt-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">Kitchen printer</h4>
                        <p className="mt-0.5 text-xs text-gray-400">
                          The printer this device prints to. Change it in Admin &rsaquo; Devices.
                        </p>
                      </div>
                      {printerHost ? (
                        <p className="font-mono text-sm text-gray-900">{printerHost}:{printerPort}</p>
                      ) : (
                        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          No printer assigned to this device. Setup will finish, but its orders
                          will queue without ever printing until one is picked under Devices.
                        </p>
                      )}
                    </div>
                  )}

                  {!canUseLocalServer && (
                    <div className="grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-gray-500">How the device works</p>
                        <p className="font-medium text-gray-900">Direct / UDP</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Server</p>
                        <p className="font-medium text-gray-900">Cloud</p>
                      </div>
                    </div>
                  )}

                  {canUseLocalServer && (
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      className="w-full text-left text-sm text-brand-green hover:underline"
                      aria-expanded={showAdvanced}
                    >
                      {showAdvanced ? '▼ Hide advanced options' : '▶ Advanced (server & mode)'}
                    </button>
                  )}

                  {canUseLocalServer && showAdvanced && (
                    <div className="space-y-4 border-t border-gray-200 pt-2">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm text-gray-500">How the device works</label>
                          <select
                            value={qrConfig.comm_mode}
                            onChange={e => setQrConfig({ ...qrConfig, comm_mode: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                          >
                            {/* <option value="ai">Voice assistant (AI)</option> */}
                            <option value="udp">Direct / UDP</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-500">Server</label>
                          <select
                            value={qrConfig.mode}
                            onChange={e => setQrConfig({ ...qrConfig, mode: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                          >
                            <option value="cloud">Cloud</option>
                            {canUseLocalServer && <option value="local">On your network (local)</option>}
                          </select>
                        </div>
                      </div>
                      {qrConfig.mode === 'local' && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-sm text-gray-500">Server IP *</label>
                            <input
                              type="text"
                              required
                              value={qrConfig.local_ip}
                              onChange={e => setQrConfig({ ...qrConfig, local_ip: e.target.value })}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                              placeholder="192.168.1.100"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm text-gray-500">Port *</label>
                            <input
                              type="number"
                              required
                              value={qrConfig.local_port}
                              onChange={e => setQrConfig({ ...qrConfig, local_port: parseInt(e.target.value, 10) || 8000 })}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 outline-none focus:border-brand-green"
                              placeholder="8000"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={loading}
                    className="w-full rounded-lg bg-brand-green px-4 py-3 font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {loading ? 'Sending to device…' : 'Send to QuickComm device'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Status box */}
          {step !== 3 && (bleStatus || selectedDeviceName) && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" aria-live="polite" aria-atomic="true">
              <h3 className="mb-2 font-semibold text-gray-900">Setup status</h3>
              {selectedDeviceName && (
                <p className="mb-1 text-sm text-gray-600">
                  Connected to: <span className="font-medium text-gray-900">{selectedDeviceName}</span>
                </p>
              )}
              {bleStatus && <p className="text-sm text-brand-green">{bleStatus}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
